import { Injectable, SecurityContext } from '@angular/core';
import { DomSanitizer, SafeValue } from '@angular/platform-browser';
import DOMPurify from 'dompurify';
import { ALL_NEWLINES_PATTERN, MULTIPLE_NEWLINES_PATTERN, MULTIPLE_SPACES_PATTERN, NON_NEWLINE_WHITESPACE_PATTERN, replaceAll } from '../model/utility';


@Injectable()
export class HtmlHelper {
  private static readonly domParser = new DOMParser();

  // img style allows for responsive images (height, object-fit, object-position)
  // maintains aspect ratio tp fit within the container
  static readonly iframeStyle = `
<style>
  body {
    font-family: "Roboto", sans-serif, Arial, Verdana;
    font-size: 14px;
    color: #333;
    background-color: #fff;
    padding: 0px;
    margin: 0px;
  }

  h1,h2,h3,h4,h5,h6 {
    font-weight: normal;
    line-height: 1.2;
  }

  a {
    color: #0073CF;
    text-decoration: none;
  }

  img {
    max-width: 100%;
    height: auto;
    object-fit: contain;
    object-position: top center;
  }
</style>
  `;

  // see: https://bitbucket.org/AlertSense/hickory/src/dev/src/BL/AlertSense.Common/Helpers/HtmlHelper.cs
  static convertPlainTextToHtml(plain: string): string {
    if (!plain) return plain;

    // replace all line breaks with the <br/> tag
    plain = plain.replace(ALL_NEWLINES_PATTERN, '<br/>')

    // wrap in <p> tags
    return '<p>' + plain + '</p>';
  }

  static convertHtmlToPlainText(html: string): string {
    if (!html) return html;
    html = this.applyFixesToPlainText(html);
    const doc = HtmlHelper.domParser.parseFromString(html, 'text/html');
    // remove any <head> <style> <script> elements
    doc.querySelectorAll('head, style, script').forEach(el => el.remove());
    html = doc.documentElement.textContent;

    // Convert all newlines to unix-style
    html = html.replace(ALL_NEWLINES_PATTERN, '\n');
    // Convert any non-newline whitespace to regular space
    html = html.replace(NON_NEWLINE_WHITESPACE_PATTERN, ' ');
    // Remove whitespace around newlines
    html = this.trimSpacesAroundNewlines(html);
    // Collapse consecutive newlines (more than 2) to exactly 2
    html = html.replace(MULTIPLE_NEWLINES_PATTERN, '\n\n');
    // Collapse multiple consecutive spaces to a single space
    html = html.replace(MULTIPLE_SPACES_PATTERN, ' ');

    return html.trim();
  }

  // manual implementation without regex to avoid super-linear runtime due to backtracking
  static trimSpacesAroundNewlines(text: string): string {
    return text
      .split('\n')
      .map(line => {
        let startIndex = 0;
        while (line[startIndex] === ' ') startIndex++;
        
        let endIndex = line.length - 1;
        while (line[endIndex] === ' ' && endIndex >= 0) endIndex--;
        
        return startIndex <= endIndex ? line.substring(startIndex, endIndex + 1) : '';
      })
      .join('\n');
  }

  static fixImgSizeCompatibility(html: string): string {
    // ROAD-6328: apply image width/height attributes for email client compatibility
    // Some native email clients (like Outlook) don't support inline CSS for styling
    // email messages. So we scan through the HTML, looking for <img> elements with
    // styles that set the width and height. If we find any, we set those values as
    // _attributes_ on the element, leaving the style intact.
    if (!html) return html;
    let hasChanges = false;
    const fixNode = (node: Node) => {
      if (node) {
        const element = node as HTMLElement;
        if (element?.tagName?.toLowerCase() === 'img') {
          let styleWidth = element.style?.width;
          if (!!styleWidth && styleWidth.endsWith('px')) {
            hasChanges = true;
            styleWidth = styleWidth.slice(0, -2); // remove 'px'
            element.setAttribute('width', styleWidth);
          }
          let styleHeight = element.style?.height;
          if (!!styleHeight && styleHeight.endsWith('px')) {
            hasChanges = true
            styleHeight = styleHeight.slice(0, -2); // remove 'px'
            element.setAttribute('height', styleHeight);
          }
        }
        if (node.hasChildNodes) {
          // recurse through child nodes
          node.childNodes.forEach(child => fixNode(child));
        }
      }
    }

    // run fixNode on the top-level element
    const body = HtmlHelper.domParser.parseFromString(html, 'text/html').body;
    fixNode(body);

    // if no changes were made, just return the original html
    return hasChanges ? (body?.innerHTML ?? html) : html;
  }

  static fixBrTags(html: string): string {
    // add closing '/', if missing
    return replaceAll(html, '<br>', '<br/>');
  }

  static fixNbsp(html: string): string {
    // no-break space (nbsp) - replace with regular space - ROAD-6278
    return html.replace(/\u00a0/g, ' ')?.replace(/&#160;/g, ' ')?.replace(/&#xa0;/g, ' ')?.replace(/&nbsp;/g, ' ');
  }

  static fixZwsp(html: string): string {
    // zero-width space (zwsp) - replace with empty string - ROAD-6322
    return html.replace(/\u200b/g, '')?.replace(/&#8203;/g, '')?.replace(/&#x200b;/g, '');
  }

  static applyFixesToHtml(html: string): string {
    html = this.fixBrTags(html);
    html = this.fixZwsp(html);
    return html;
  }

  static applyFixesToPlainText(plain: string): string {
    plain = this.fixNbsp(plain);
    plain = this.fixZwsp(plain);
    return plain;
  }

  static customSanitize(html: string): string {
    return DOMPurify.sanitize(html);
  }

  static areEquivalent(html1: string, html2: string): boolean {
    if (!!html1 !== !!html2) return false;
    if (html1 === html2) return true;
    return this.normalizeHtml(html1) === this.normalizeHtml(html2);
  }

  static normalizeHtml(html: string): string {
    if (!html) return html;
    html = this.applyFixesToPlainText(html);
    const doc = HtmlHelper.domParser.parseFromString(html, 'text/html');
    return doc.documentElement.innerHTML;
  }

  constructor(
    private readonly sanitizer: DomSanitizer,
  ) { }

  unwrapSafeValue(html: SafeValue): string {
    // See https://stackoverflow.com/a/53079256
    return this.sanitizer.sanitize(SecurityContext.HTML, html);
  }

  processRichContent(html: string, sanitize: boolean = true, css: string = HtmlHelper.iframeStyle): SafeValue {
    if (html) {
      // first, (maybe) sanitize the HTML to remove unsafe content
      html = sanitize ? HtmlHelper.customSanitize(html) : html;
      // open links in a new window (adds target="_blank" to all <a href);
      html = ('' + html).replace(/<a\s+href=/gi, '<a target="_blank" href=');
      // append a basic (trusted style)
      html = html + (css ?? '');

      // convert to a SafeValue so that we can display it in an iframe
      return this.sanitizer.bypassSecurityTrustHtml(html);
    }

    return html;
  }
}
